import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Sparkles, Search, Filter, Settings2, Plus } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { PageBody } from "@/components/dashboard/DashboardShell";
import { loadAdminAIFn, type Convo } from "./-dashboard.admin.ai.functions";

export const Route = createFileRoute("/dashboard/admin/ai")({
  component: AdminAI,
});

function AdminAI() {
  const { user, loading, primaryRole } = useAuth();
  const [convos, setConvos] = useState<Convo[]>([]);
  const [voiceCount, setVoiceCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [requestsCount, setRequestsCount] = useState(0);
  const [missedCount, setMissedCount] = useState(0);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (loading || primaryRole !== "admin") return;
    (async () => {
      const { convos: list, missedCount, requestsCount } = await loadAdminAIFn();
      setConvos(list);
      setChatCount(list.filter((x) => x.mode === "chat").length);
      setVoiceCount(list.filter((x) => x.mode === "voice").length);
      setMissedCount(missedCount);
      setRequestsCount(requestsCount);
    })();
  }, [loading, primaryRole]);

  if (loading || !user || primaryRole !== "admin") return null;

  const filtered = convos.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      c.mode.toLowerCase().includes(q) ||
      c.status.toLowerCase().includes(q) ||
      (c.summary ?? "").toLowerCase().includes(q) ||
      (c.language ?? "").toLowerCase().includes(q)
    );
  });

  const stats = [
    { label: "Chat conversations", value: chatCount },
    { label: "Voice calls", value: voiceCount },
    { label: "Appointment requests", value: requestsCount },
    { label: "Missed bookings", value: missedCount },
  ];

  const columns = ["Conversation", "Mode", "Language", "Status", "User", "Created"];

  return (
    <AdminPageShell title="Chatbot" description="Conversations, voice calls, and missed bookings from the AI chatbot.">
      <PageBody className="space-y-4 pt-4">



        {/* Sub-tabs + actions */}
        <div className="flex items-center justify-between gap-4 border-b border-border pb-0">
          <div className="flex items-center gap-6">
            <button className="relative text-[13px] font-semibold text-primary pb-2.5">
              Chatbot
              <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary" />
            </button>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="h-8 pl-8 pr-3 text-[12px] bg-background border border-border w-[220px] focus:outline-none focus:border-primary"
              />
            </div>
            <button className="h-8 w-8 inline-flex items-center justify-center border border-border text-muted-foreground hover:text-foreground" aria-label="Filter">
              <Filter className="h-3.5 w-3.5" />
            </button>
            <button className="h-8 w-8 inline-flex items-center justify-center border border-border text-muted-foreground hover:text-foreground" aria-label="Settings">
              <Settings2 className="h-3.5 w-3.5" />
            </button>
            <button className="h-8 inline-flex items-center gap-1.5 px-3 text-[12px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-3.5 w-3.5" /> Configure
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="card-surface-flat rounded-md border border-border bg-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {s.label}
              </div>
              <div className="text-[22px] font-bold tabular-nums mt-1.5 leading-none">
                {s.value ?? "—"}
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="card-surface rounded-md border border-border bg-card overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2.5 w-8">
                  <input type="checkbox" className="h-3.5 w-3.5" disabled />
                </th>
                {columns.map((c) => (
                  <th key={c} className="px-3 py-2.5 font-semibold whitespace-nowrap">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-16 text-center text-muted-foreground text-[13px]">
                    Chatbot conversations will appear here.
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-3 py-2.5">
                      <input type="checkbox" className="h-3.5 w-3.5" />
                    </td>
                    <td className="px-3 py-2.5 max-w-[420px] truncate" title={c.summary ?? c.id}>
                      {c.summary ?? c.id}
                    </td>
                    <td className="px-3 py-2.5 capitalize">{c.mode}</td>
                    <td className="px-3 py-2.5 uppercase">{c.language ?? "EN"}</td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-medium bg-muted text-foreground capitalize">
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{c.user_id ? "User" : "Guest"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                      {new Date(c.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </AdminPageShell>
  );
}
